// INV26 Slice 1 (docs/build-briefs/INV26-backbar-inventory.md; DEC-206
// item 2, DEC-207): the Settings inventory list's sort. Pure, dependency
// free (no `window`/`document` access), the same established shape
// ui/src/sizeTokens.ts and ui/src/liquorRowPricing.ts already use, split
// out of admin.ts for the identical reason sizeTokens.ts's own header
// documents: admin.ts is NOT DOM-free at import (396 module-level
// mustFind(...) calls resolve real DOM elements as soon as the module
// loads), so a plain `node:test` file cannot import a comparator defined
// inline in admin.ts. admin.ts imports the five exports below rather than
// defining this logic itself.
//
// Scope: exactly the five columns the brief names (Item, Package, On
// hand, Par, Status), one active sort, ascending/descending toggle on
// repeat click of the same column, matching the mockup's
// design/mockups/inv26-inventory-backbar.html header affordance. No
// type/subtype column exists here to sort by, per DEC-206 item 2.

import { compareNames } from "./nameSort.ts";

/** liquor_products.size_value/size_unit converted to a common millilitre
 * base for the Package column's numeric sort, the same ml/l multiplier
 * ui/src/sizeTokens.ts's own SIZE_UNIT_MULTIPLIER uses for the unrelated
 * serving_size token (that one also carries "oz", which a container size
 * never does: size_unit is written only via SIZE_UNIT_DISPLAY's own
 * ml/l/oz keys, and a container is always ml or l in the seeded/real
 * data -- an "oz" container size, were one ever entered, falls through to
 * the unresolved-multiplier branch below and sorts last like any other
 * unresolved size, not silently mis-scaled as ml). Null (sorts last, see
 * compareNullable) when size_value or size_unit is missing, non-numeric, or an
 * unrecognized unit -- covers a keg item, whose size lives in the
 * default_keg_size vocabulary word rather than these two columns; see
 * compareNullable below for how a null sort key is placed. */
const PACKAGE_UNIT_TO_ML: Readonly<Record<string, number>> = { ml: 1, l: 1000 };

export function packageAmountMl(sizeValue: string | null, sizeUnit: string | null): number | null {
  if (sizeValue === null || sizeUnit === null) {
    return null;
  }
  const amount = Number(sizeValue);
  if (!Number.isFinite(amount)) {
    return null;
  }
  const multiplier = PACKAGE_UNIT_TO_ML[sizeUnit];
  return multiplier === undefined ? null : amount * multiplier;
}

export type InventoryListSortColumn = "item" | "package" | "onHand" | "par" | "status";
export type InventoryListSortDirection = "asc" | "desc";

export interface InventoryListSortState {
  column: InventoryListSortColumn;
  direction: InventoryListSortDirection;
}

/** The sort-relevant primitives for one row. admin.ts computes these from
 * a LiquorProductDto (display formatting -- "750 ml bottle", "2.5
 * bottles" -- stays in admin.ts; this module only ever sees the numbers
 * and label that formatting is built from) and passes them alongside
 * whatever richer object it actually wants reordered, via the generic
 * `T extends InventoryListSortKeys` on sortInventoryListRows below. */
export interface InventoryListSortKeys {
  itemName: string;
  /** The package's size normalized to a common unit (millilitres) for
   * comparison, e.g. 750 for "750 ml", 1750 for "1.75 L". Null when no
   * comparable numeric size exists (a keg item's own size lives in a
   * vocabulary word, not size_value/size_unit, and a product with no
   * size configured at all) -- always sorts last, in either direction,
   * via compareNullable below, rather than colliding with a real 0. */
  packageAmount: number | null;
  /** onHandInCountUnit, parsed to a number. Null when the server could
   * not resolve a count unit (StockStateRow.onHandInCountUnit is null:
   * no size/keg configuration, or a genuinely mixed-unit product) --
   * always sorts last. */
  onHand: number | null;
  /** par_level, parsed to a number. Null when the item has no par set --
   * always sorts last, matching quantityStateOf's own "no par means
   * never low" null guard: an untracked item is not "low", and it is not
   * meaningfully less-than or greater-than a tracked one either. */
  par: number | null;
  /** inventoryListStatusRank's own output for this row's status; see
   * that function below for the four values and their order. */
  statusRank: number;
}

const NULL_SORTS_LAST = Number.POSITIVE_INFINITY;

/** Compares two nullable numeric keys so a null value ALWAYS lands after
 * every real value, in BOTH ascending and descending order -- never
 * merely "last when ascending", which is what a blanket
 * `.sort(...).reverse()` for descending would silently produce instead (a
 * null row would jump to the FRONT on reverse, since reverse flips the
 * whole array including the trailing nulls; caught by this file's own
 * pre-fix test run, recorded in the build record). The numeric part is
 * negated for descending; the "null is not a magnitude" part is not. */
function compareNullable(
  a: number | null,
  b: number | null,
  direction: InventoryListSortDirection,
): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  const diff = a - b;
  return direction === "desc" ? -diff : diff;
}

function compareRows(
  a: InventoryListSortKeys,
  b: InventoryListSortKeys,
  column: InventoryListSortColumn,
  direction: InventoryListSortDirection,
): number {
  // UX88 (DEC-339): the shared name sort (case-insensitive, numbers as numbers).
  const nameOrder =
    direction === "desc"
      ? -compareNames(a.itemName, b.itemName)
      : compareNames(a.itemName, b.itemName);
  switch (column) {
    case "item":
      return nameOrder;
    case "package": {
      const diff = compareNullable(a.packageAmount, b.packageAmount, direction);
      return diff !== 0 ? diff : nameOrder;
    }
    case "onHand": {
      const diff = compareNullable(a.onHand, b.onHand, direction);
      return diff !== 0 ? diff : nameOrder;
    }
    case "par": {
      const diff = compareNullable(a.par, b.par, direction);
      return diff !== 0 ? diff : nameOrder;
    }
    case "status": {
      const diff = compareNullable(a.statusRank, b.statusRank, direction);
      return diff !== 0 ? diff : nameOrder;
    }
  }
}

/** Sorts a copy of `rows` (the input array is never mutated) by `column`,
 * ascending or descending. A null Package/On hand/Par/Status value always
 * sorts last, in EITHER direction (compareNullable above); every column
 * other than Item ties back to the item name, itself flipped for
 * descending so a tie-broken descending sort reads Z-to-A within the tied
 * group rather than A-to-Z, so the order is fully deterministic
 * regardless of the input array's own starting order or
 * Array.prototype.sort's stability, and two items sharing a Package size,
 * On hand figure, Par, or Status still land in a predictable, testable
 * order rather than "whatever the network happened to return them in". */
export function sortInventoryListRows<T extends InventoryListSortKeys>(
  rows: readonly T[],
  column: InventoryListSortColumn,
  direction: InventoryListSortDirection,
): T[] {
  return [...rows].sort((a, b) => compareRows(a, b, column, direction));
}

/** Header-click behavior: clicking a column that is not the current sort
 * switches to it, ascending. Clicking the CURRENT column's own header
 * toggles direction. Always exactly one active sort (never two columns,
 * never "unsorted"), matching the mockup's aria-sort affordance (one
 * header carries "ascending"/"descending"; every other carries "none"
 * with a quiet double-caret). */
export function nextInventoryListSort(
  current: InventoryListSortState,
  clickedColumn: InventoryListSortColumn,
): InventoryListSortState {
  if (current.column !== clickedColumn) {
    return { column: clickedColumn, direction: "asc" };
  }
  return { column: clickedColumn, direction: current.direction === "asc" ? "desc" : "asc" };
}

/** The Status column's four values (DEC-206 item 2's "OK / Low / Out /
 * Negative"), derived from INV20's own par-based quantityState plus
 * whether the converted on-hand figure is negative -- never a new
 * domain-level state. `quantityStateOf` (src/domain/inventoryStockState.ts)
 * already collapses "at or below zero" into a single "out_of_stock" for
 * every domain purpose (selection blocking, the Low list, generateDraft
 * Proposals); Negative is a DISPLAY-only refinement of that same state,
 * using the existing isNegativeQuantity check (UX16-F2/DEC-178), so a
 * negative on-hand item is still "out_of_stock" everywhere else in the
 * system and only reads as the more specific "Negative" chip here.
 * Because quantity <= 0 is what makes quantityState "out_of_stock" in the
 * first place, "low" and "ok" can never be negative and this function
 * never has to consider that combination. Returns null when quantityState
 * itself is null (a mixed-unit product with no derivable state), matching
 * StockStateRow's own null-propagation contract. */
/** INVLOC1-S2 (DEC-326): "noPar" is a DISPLAY word for an item that is in stock and has no par at the shown location: it is
 * never Low (no par means nothing to be low against), and it is not OK either, because OK claims a comparison that was
 * never made. Out, Negative and 86'd still win over it (zero on hand is Out whatever the par). */
export type InventoryListStatus = "flagged86" | "out" | "negative" | "low" | "atPar" | "ok" | "noPar" | "noStartingStock";

/** LOCFLOOR1 (sweep B4): "Low" means below par. The server's own reading (quantityStateOf, DEC-176) is inclusive, so an
 * item exactly at par is still in the reorder list with a zero shortfall; a screen that says Low next to a disabled
 * "At par" contradicts itself. This is the one test every screen uses for that case: on hand at or above a par that is
 * set, and something on hand. Unknown on hand or par is never at par. */
export function isAtPar(onHand: number | null, par: number | null): boolean {
  return onHand !== null && par !== null && onHand > 0 && onHand >= par;
}

/** NIGHTFIX1 (3): the floor's reading of the server's quantity state. The server's "low" is inclusive of par (DEC-176), so
 * the floor applies the same strict rule as Stock levels: an item the server calls low that sits exactly at par (or above)
 * reads ok. Unknown on hand or par keeps the server's word. Both are in the item's count unit (par_level, onHandInCountUnit). */
export function floorQuantityState(
  quantityState: "out_of_stock" | "low" | "ok" | null,
  onHandInCountUnit: string | null | undefined,
  parLevel: string | null | undefined,
): "out_of_stock" | "low" | "ok" | null {
  if (quantityState !== "low") return quantityState;
  const num = (v: string | null | undefined): number | null => {
    if (v === null || v === undefined || v.trim() === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return isAtPar(num(onHandInCountUnit), num(parLevel)) ? "ok" : "low";
}

/** STK1 (DEC-269): a manager's explicit 86 flag wins over every derived
 * state, the same rule the floor's stockStateBadge already follows
 * (flagged86 always wins). It is a stated fact, not a ledger reading, so it
 * shows even for a mixed-unit product whose quantityState is null, and an
 * item can read 86'd with stock on the shelf ("86'd with 4 bottles on
 * hand"); On hand stays visible beside it so both facts are legible. */
export function inventoryListStatus(
  quantityState: "out_of_stock" | "low" | "ok" | null,
  onHand: number | null,
  flagged86: boolean,
  /** INVLOC1-S2: false for an item with no par at the shown location. Defaults to true so a caller that does not say keeps
   * the word it always had. */
  hasPar = true,
  /** LOCFLOOR1: the par in the same unit as onHand. When given, an item the server calls low that sits exactly at par reads At par. */
  par: number | null = null,
  /** MGRDAYMAJ1 (manager-day-12): the item has never had stock history at a location still entering starting stock, so its
   * zero is "not entered yet", a neutral state of its own, and not Out. */
  noStartingStock = false,
): InventoryListStatus | null {
  if (flagged86) {
    return "flagged86";
  }
  if (quantityState === null) {
    return null;
  }
  if (quantityState === "out_of_stock") {
    if (noStartingStock && (onHand === null || onHand === 0)) return "noStartingStock";
    return onHand !== null && onHand < 0 ? "negative" : "out";
  }
  if (quantityState === "ok" && !hasPar) {
    return "noPar";
  }
  if (quantityState === "low" && isAtPar(onHand, par)) {
    return "atPar";
  }
  return quantityState;
}

/** Ascending sort order for the Status column: most severe first (86'd, Out,
 * then Negative, then Low, then OK), the order a manager scanning for
 * problems wants read top to bottom. A null status (mixed units,
 * genuinely undetermined) sorts last in either direction, via
 * compareNullable/NULL_SORTS_LAST in compareRows above, same as every other
 * column's own "unknown sorts last" rule. */
const STATUS_RANK: Readonly<Record<InventoryListStatus, number>> = {
  flagged86: 0,
  out: 1,
  negative: 2,
  low: 3,
  noStartingStock: 3.5,
  atPar: 4,
  ok: 5,
  noPar: 6,
};

export function inventoryListStatusRank(status: InventoryListStatus | null): number {
  return status === null ? NULL_SORTS_LAST : STATUS_RANK[status];
}

export const INVENTORY_LIST_STATUS_LABEL: Readonly<Record<InventoryListStatus, string>> = {
  flagged86: "86'd",
  out: "Out",
  negative: "Negative",
  low: "Low",
  atPar: "At par",
  ok: "OK",
  noPar: "No par",
  noStartingStock: "Not counted yet",
};
